import React, { useState } from 'react';
import { Alert, Badge, Button, Card, Col, ListGroup, Row } from 'react-bootstrap';
import {
  circleStatusLabel,
  formatDateTime,
  getCircleReadiness,
} from './recoveryUi.ts';
import type {
  MembershipStatus,
  RecoveryCircle,
  RecoveryCircleMember,
} from './types.ts';

function membershipVariant(status: MembershipStatus): string {
  if (status === 'accepted') return 'success';
  if (status === 'declined' || status === 'revoked') return 'danger';
  return 'warning';
}

function membershipLabel(status: MembershipStatus): string {
  const labels: Record<MembershipStatus, string> = {
    invited: 'Invited — response pending',
    accepted: 'Accepted',
    declined: 'Declined',
    revoked: 'Removed',
  };
  return labels[status];
}

function memberName(member: RecoveryCircleMember, index: number): string {
  if (member.displayName) return member.displayName;
  if (member.holderType === 'institution') return `Institution ${index + 1}`;
  return `Personal keyholder ${index + 1}`;
}

export interface CircleStatusProps {
  circle: RecoveryCircle;
  onActivate?: (circle: RecoveryCircle) => Promise<void>;
  onRotate?: (circle: RecoveryCircle) => Promise<void>;
  onRetire?: (circle: RecoveryCircle) => Promise<void>;
}

export default function CircleStatus({
  circle,
  onActivate,
  onRotate,
  onRetire,
}: CircleStatusProps) {
  const readiness = getCircleReadiness(circle);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allAccepted =
    readiness.total >= 2 && readiness.accepted === readiness.total;
  const canStartActivation =
    circle.status !== 'active' &&
    allAccepted &&
    !circle.rotationRequired &&
    circle.status !== 'disabled' &&
    Boolean(onActivate);

  const handleActivate = async () => {
    if (!onActivate || !canStartActivation) return;
    setBusy(true);
    setError(null);
    try {
      await onActivate(circle);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Could not prepare circle activation.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="owned-circle-status-heading">
      <Card className="recovery-status-card">
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
            <div>
              <h3 id="owned-circle-status-heading" className="h4 mb-1">
                Circle status
              </h3>
              <p className="text-body-secondary mb-0">
                Created {formatDateTime(circle.createdAt)}
              </p>
            </div>
            <Badge
              bg={
                circle.rotationRequired
                  ? 'warning'
                  : circle.status === 'active'
                    ? 'success'
                    : 'secondary'
              }
              text={circle.rotationRequired ? 'dark' : undefined}
            >
              {circle.rotationRequired
                ? 'Rotation required'
                : circleStatusLabel(circle.status)}
            </Badge>
          </div>

          <Row className="g-3 my-2">
            <Col sm={4}>
              <div className="recovery-stat">
                <span className="recovery-stat__value">
                  {circle.threshold} of {readiness.total}
                </span>
                <span className="recovery-stat__label">must approve</span>
              </div>
            </Col>
            <Col sm={4}>
              <div className="recovery-stat">
                <span className="recovery-stat__value">
                  {readiness.accepted} accepted
                </span>
                <span className="recovery-stat__label">
                  {readiness.invited} pending · {readiness.declined} declined
                </span>
              </div>
            </Col>
            <Col sm={4}>
              <div className="recovery-stat">
                <span className="recovery-stat__value">
                  {circle.revealMemberNames ? 'Named roster' : 'Private roster'}
                </span>
                <span className="recovery-stat__label">
                  Size and threshold are always disclosed
                </span>
              </div>
            </Col>
          </Row>

          {circle.rotationRequired && (
            <Alert variant="warning">
              <strong>Recovery-key rotation is required.</strong> Completing
              recovery consumed this version. Same keyholders keep their
              membership if they still have local custody. Starting from
              scratch is optional.
            </Alert>
          )}

          <h4 className="h5 mt-4">Keyholder responses</h4>
          <ListGroup className="mb-4">
            {circle.members.map((member, index) => (
              <ListGroup.Item
                key={member.membershipId || member.id}
                className="d-flex flex-wrap justify-content-between gap-2"
              >
                <span>
                  <strong>{memberName(member, index)}</strong>
                  <span className="d-block small text-body-secondary">
                    {member.holderType === 'institution'
                      ? 'Institution'
                      : 'Personal keyholder'}
                  </span>
                </span>
                <Badge
                  bg={membershipVariant(member.status)}
                  className="align-self-center"
                >
                  {membershipLabel(member.status)}
                </Badge>
              </ListGroup.Item>
            ))}
          </ListGroup>

          {circle.rotationRequired ? (
            <>
              <Alert variant="warning">
                <strong>Rotate the recovery key on this device.</strong> Adult-chat
                keys are already restored here. Rotation issues a new recovery
                secret and new sealed shares to the same keyholders. Anyone who
                lost this browser’s custody key will not get a silent
                replacement; they must accept a new invitation. Retire only if
                you want to start a new circle.
              </Alert>
              {onRotate ? (
                <Button
                  className="me-2 mb-2"
                  disabled={busy || !readiness.canRotate}
                  onClick={async () => {
                    setBusy(true);
                    setError(null);
                    try {
                      await onRotate(circle);
                    } catch (cause) {
                      setError(
                        cause instanceof Error
                          ? cause.message
                          : 'Could not rotate this recovery circle.'
                      );
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {busy ? 'Rotating…' : 'Rotate recovery key'}
                </Button>
              ) : (
                <Alert variant="secondary">
                  Rotation is unavailable until device crypto can wrap the
                  restored adult-chat keys.
                </Alert>
              )}
              {onRetire ? (
                <Button
                  variant="outline-secondary"
                  className="mb-2"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError(null);
                    try {
                      await onRetire(circle);
                    } catch (cause) {
                      setError(
                        cause instanceof Error
                          ? cause.message
                          : 'Could not retire this circle.'
                      );
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {busy ? 'Retiring…' : 'Retire and start a new circle'}
                </Button>
              ) : null}
            </>
          ) : circle.status === 'active' ? (
            <Alert variant="success" className="mb-0">
              Active. A request still needs the owner’s authenticated lockout
              flow and {circle.threshold} fresh approvals; circle membership
              alone cannot start recovery.
            </Alert>
          ) : (
            <>
              <p>
                {allAccepted
                  ? 'Every keyholder has accepted. Device crypto may now prepare the encrypted activation manifest and one encrypted envelope per membership.'
                  : 'Activation waits until every selected keyholder accepts. Reaching the approval threshold is not enough to distribute the circle safely.'}
              </p>
              <Button
                onClick={handleActivate}
                disabled={!canStartActivation || busy}
              >
                {busy
                  ? 'Preparing encrypted activation…'
                  : 'Prepare encrypted activation'}
              </Button>
              {!onActivate && (
                <p className="small text-body-secondary mt-2 mb-0">
                  Unavailable until the parent device-crypto integration
                  supplies an encrypted manifest and encrypted envelopes. No
                  placeholder recovery material will be sent.
                </p>
              )}
            </>
          )}

          <div aria-live="polite" aria-atomic="true">
            {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
          </div>
        </Card.Body>
      </Card>
    </section>
  );
}
